import React from 'react';
import { Link } from 'react-router-dom';

import { formatCurrency } from '~libs/dataUtils';

import { Column } from '~components/table-view-v3';
import { colourStack } from '~support-at-home/components/budgets/utils';

import {
  DashboardBudgetOverview,
  DashboardCmClient,
  DashboardCmOverview,
  DashboardCmWorker,
  DashboardOverUtilisedBudget,
} from '~support-at-home/models/dashboard';

export const transformCompanyOverviewPieData = (overview: DashboardBudgetOverview) => [
  { id: 'Booked', label: 'Booked', value: overview.booked, color: colourStack[0] },
  { id: 'Claimed', label: 'Claimed', value: overview.claimed, color: colourStack[1] },
  { id: 'Remaining', label: 'Remaining', value: overview.remaining, color: colourStack[2] },
];

export const transformCmOverviewPieData = (overview: DashboardCmOverview) => [
  { id: 'Used', label: 'Used', value: overview.used, color: colourStack[1] },
  { id: 'Remaining', label: 'Remaining', value: overview.remaining, color: colourStack[2] },
];

export const overUtilisedColumns: Column[] = [
  { id: 'client_name', header: 'Client', type: 'readonly' },
  { id: 'funding_type', header: 'Funding Type', type: 'readonly' },
  {
    id: 'utilisation_rate',
    header: 'Utilisation',
    type: 'readonly',
    classNames: {
      header: 'cell-align-right',
      column: 'cell-align-right',
    },
    deriveValue: (row) => `${((row.utilisation_rate as number) * 100).toFixed(1)}%`,
  },
  {
    id: 'budget_id',
    header: 'Tracker',
    type: 'readonly',
    classNames: {
      header: 'cell-align-center',
      column: 'cell-align-center',
    },
    renderReadOnly: (_value, row) => (
      <Link to={`/support-at-home/budgets/${row.budget_id}/tracker`} aria-label={`View tracker for ${row.client_name}`}>
        View
      </Link>
    ),
  },
];

export const cmClientColumns: Column[] = [
  { id: 'client_name', header: 'Client', type: 'readonly' },
  {
    id: 'cm_available',
    header: 'CM Allocation',
    type: 'readonly',
    classNames: {
      header: 'cell-align-right',
      column: 'cell-align-right',
    },
    deriveValue: (row) => formatCurrency(row.cm_available as number),
  },
  {
    id: 'cm_used',
    header: 'CM Used',
    type: 'readonly',
    classNames: {
      header: 'cell-align-right',
      column: 'cell-align-right',
    },
    deriveValue: (row) => formatCurrency(row.cm_used as number),
  },
  {
    id: 'cm_utilisation_rate',
    header: 'Utilisation',
    type: 'readonly',
    classNames: {
      header: 'cell-align-right',
      column: 'cell-align-right',
    },
    deriveValue: (row) => `${((row.cm_utilisation_rate as number) * 100).toFixed(1)}%`,
  },
  {
    id: 'budget_id',
    header: 'Tracker',
    type: 'readonly',
    classNames: {
      header: 'cell-align-center',
      column: 'cell-align-center',
    },
    renderReadOnly: (_value, row) => (
      <Link to={`/support-at-home/budgets/${row.budget_id}/tracker`} aria-label={`View tracker for ${row.client_name}`}>
        View
      </Link>
    ),
  },
];

export const cmWorkersColumns: Column[] = [
  { id: 'worker_name', header: 'Worker', type: 'readonly' },
  {
    id: 'booked',
    header: 'Booked',
    type: 'readonly',
    classNames: {
      header: 'cell-align-right',
      column: 'cell-align-right',
    },
    deriveValue: (row) => formatCurrency(row.booked as number),
  },
  {
    id: 'claimed',
    header: 'Claimed',
    type: 'readonly',
    classNames: {
      header: 'cell-align-right',
      column: 'cell-align-right',
    },
    deriveValue: (row) => formatCurrency(row.claimed as number),
  },
  {
    id: 'total',
    header: 'Total',
    type: 'readonly',
    classNames: {
      header: 'cell-align-right',
      column: 'cell-align-right',
    },
    deriveValue: (row) => formatCurrency(row.total as number),
  },
];

export const mapOverUtilisedToRows = (data: DashboardOverUtilisedBudget[]) =>
  data.map((item) => ({
    id: item.budget_id,
    client_name: item.client_name,
    funding_type: item.funding_type,
    utilisation_rate: item.utilisation_rate,
    budget_id: item.budget_id,
  }));

export const mapCmClientsToRows = (data: DashboardCmClient[]) =>
  data.map((item) => ({
    id: item.budget_id,
    client_name: item.client_name,
    cm_available: item.cm_available,
    cm_used: item.cm_used,
    cm_utilisation_rate: item.cm_utilisation_rate,
    budget_id: item.budget_id,
  }));

export const mapCmWorkersToRows = (data: DashboardCmWorker[]) =>
  data.map((item) => ({
    id: item.worker_id,
    worker_name: item.worker_name,
    booked: item.booked,
    claimed: item.claimed,
    total: item.total,
  }));

export const getCurrentQuarter = (): number => Math.ceil((new Date().getMonth() + 1) / 3);

export const getCurrentYear = (): number => new Date().getFullYear();

export const getCurrentPeriod = (): { quarter: number; year: number } => {
  const now = new Date();
  return {
    quarter: Math.ceil((now.getMonth() + 1) / 3),
    year: now.getFullYear(),
  };
};
